import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TailSpin, ThreeDots } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { isEmpty } from 'lodash-es';

import boonLogo from '../../assets/images/boon_logo.png';
import Checkered from '../../assets/images/Checkered.svg';
import Desk from '../../assets/images/DeskPic.svg';
import Line from '../../assets/images/DottedLine.svg';
import File from '../../assets/images/File.svg';
import importIcon from '../../assets/images/import.svg';
import linkedinLogo from '../../assets/images/linkedin-logo.png';
import Pad from '../../assets/images/NotePad.svg';
import Tick from '../../assets/images/TickStep.svg';
import { ALLOWED_FORMAT, LINKEDIN_COMPANY_URL, MAX_IMAGE_SIZE } from '../../constants/commonConstants';
import { findSelectedItem, handleLogoUpload, mapDropdownValues } from '../../helpers/commonHelper';
import { getLinkedinData, resetLinkedInLoader } from '../../redux/actions/linkedInImportActions';
import { getDropdownValues, saveOrganizationDetails } from '../../redux/actions/organizationActions';
import { industryCreation } from '../../services/industriesService';
import Notification from '../Notification/Notification';
import Button from '../shared/Button/Button';
import ButtonWithIcon from '../shared/ButtonWithIcon/ButtonWithIcon';
import CustomSelect from '../shared/CustomSelect/CustomSelect';
import CustomTextField from '../shared/CustomTextField/CustomTextField';
import LocationSelect from '../shared/Location/LocationSelect';

import styles from './CreateOrganization.module.scss';

const CreateOrganization = () => {
  const { t } = useTranslation('organization');
  const dispatch = useDispatch();
  const initialState = { selectPhotoFor: null, file: null, preview: null };
  const [avatarFileObj, setAvatarFileObj] = useState(initialState);
  const fileInputRef = useRef(null);
  const organizationLoading = useSelector((state) => state.organizationsReducer.organizationCreationLoading);
  const dropDownValues = useSelector((state) => state.organizationsReducer.dropdownValues);
  const [logoPreview, setLogoPreview] = useState(null);
  const [url, setUrl] = useState('');
  const regex = /^[a-zA-Z0-9_%.\-&]*$/;
  const isURLValid = regex.test(url);
  const [industryImport, setIndustryImport] = useState(null);
  const [industryLocation, setIndustryLocation] = useState(null);
  const [fetchLinkedin, setFetchLinkedIn] = useState(null);
  const [organizationSize, setOrganizationSize] = useState(null);
  const currentUser = useSelector((state) => state.authReducer.currentUser);
  const linkedInLoading = useSelector((state) => state.linkedInImportReducer.loading);
  const [logoUpLoading, setLogoUploading] = useState(false);
  const initialValues = {
    name: null,
    overview: null,
    industry_id: null,
    logo_image_id: null,
    locations: [],
    organization_size_id: null,
  };
  useEffect(() => {
    // Reset loading state on mount in case it was persisted from an interrupted request
    dispatch(resetLinkedInLoader());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getDropdownValues());
  }, [currentUser]);

  const importData = () => {
    dispatch(
      getLinkedinData(
        url,
        (data) => {
          const fetchedData = data.linkedin_data || {};
          if (isEmpty(fetchedData)) {
            Notification.error(t('common:notifications.noCompanyDataFound'));
          } else {
            setFetchLinkedIn(fetchedData);
          }
        },
        () => {
          // Error already shown by the action via Notification
        }
      )
    );
  };

  const extractCompanyName = (linkedinUrl) => {
    let trimmedUrl = linkedinUrl.replace(/^(https?:\/\/)?(www\.)?linkedin\.com\//, '');

    if (trimmedUrl.startsWith('/')) {
      trimmedUrl = trimmedUrl.slice(1);
    }

    if (trimmedUrl.startsWith('company/')) {
      trimmedUrl = trimmedUrl.replace('company/', '');
    }

    trimmedUrl = trimmedUrl.replace(/\/$/, '');

    return trimmedUrl;
  };

  const handleUrlChange = (e) => {
    const rawUrl = e.target.value;
    const companyName = extractCompanyName(rawUrl);
    setUrl(companyName);
  };

  const UpdateFormikValues = () => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
      if (fetchLinkedin) {
        setFieldValue('name', fetchLinkedin.name);
        setFieldValue('industry_id', fetchLinkedin.industry);
        setFieldValue('organization_size_id', fetchLinkedin.companysize);
        if (fetchLinkedin.description) {
          setFieldValue('overview', fetchLinkedin.description);
        }
      }
    }, [fetchLinkedin, setFieldValue]);

    return null;
  };

  const checkFormValues = (values) => {
    const { locations, organization_size_id, industry_id, name } = values;

    return !(locations?.length && organization_size_id && industry_id && name);
  };

  const handleSubmit = useCallback(
    async (values) => {
      let industryId;

      if (checkFormValues(values)) {
        Notification.error(t('createOrganization.errors.fillAllFields'));
        return;
      }
      const selectedIndustry = findSelectedItem(dropDownValues.organization_industries, values.industry_id);
      const selectedSize = findSelectedItem(dropDownValues.organization_sizes, values.organization_size_id);
      if (!selectedIndustry) {
        industryId = await industryCreation(values.industry_id);
      }
      setLogoUploading(true);
      const logoImageId = await handleLogoUpload(avatarFileObj, logoPreview);
      const formValues = {
        ...values,
        logo_image_id: logoImageId,
        overview: values.overview || null,
        industry_id: selectedIndustry ? selectedIndustry.id : industryId?.data?.payload?.industry_id,
        organization_size_id: selectedSize ? selectedSize.id : null,
      };
      setLogoUploading(false);
      dispatch(saveOrganizationDetails(formValues));
    },
    [dropDownValues, logoPreview]
  );

  const handleLogoChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      if (!ALLOWED_FORMAT.includes(file.type)) {
        return Notification.error(t('createOrganization.errors.invalidImageFormat'));
      }
      if (file.size > MAX_IMAGE_SIZE) {
        Notification.error(t('createOrganization.errors.imageSizeExceeded'));
        return;
      }
      setLogoPreview(URL.createObjectURL(file));
      setAvatarFileObj({ selectPhotoFor: 'logo_image', file });
      setFieldValue('companyLogo', file);
    }
  };

  const handleLocationChange = (locations, setFieldValue) => {
    const updatedLocations = { ...locations };
    if (!isEmpty(locations)) {
      updatedLocations.country = locations?.country_name;
      updatedLocations.location_type = 0;
      setFieldValue('locations', [updatedLocations]);
    } else {
      setFieldValue('locations', null);
    }
  };

  const triggerFileInput = () => {
    document.getElementById('fileInput').click();
  };

  useEffect(() => {
    if (fetchLinkedin?.industry) {
      const industryImportFetch = [
        {
          label: fetchLinkedin.industry,
          value: fetchLinkedin.industry,
        },
      ];
      setIndustryImport(industryImportFetch);
    }
    if (fetchLinkedin?.full_address) {
      const industryImportLocation = {
        full_address: fetchLinkedin?.full_address,
      };
      setIndustryLocation(industryImportLocation);
    }
    if (fetchLinkedin?.companysize) {
      const industrySize = [
        {
          label: fetchLinkedin.companysize,
          value: fetchLinkedin.companysize,
        },
      ];
      setOrganizationSize(industrySize);
    }
    if (fetchLinkedin?.avatar) {
      setLogoPreview(fetchLinkedin?.avatar);
      setAvatarFileObj(initialState);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [fetchLinkedin]);
  const industryOptions = mapDropdownValues(dropDownValues.organization_industries);
  const industrySizes = mapDropdownValues(dropDownValues.organization_sizes);

  if (dropDownValues.length === 0) {
    return null;
  }
  return (
    <div className={styles.pageContainerOrg}>
      {(organizationLoading || logoUpLoading) && (
        <div className={styles.loaderContainer}>
          <ThreeDots />
        </div>
      )}
      <div className={organizationLoading ? styles.dimmedForm : ''}>
        <div className={styles.mainHeader}>
          <img src={boonLogo} alt={t('createOrganization.altText.boonLogo')} />
        </div>
        <div className={styles.mainBody}>
          <div className={styles.infoFlexItem}>
            <div className={styles.mainInfoContainer}>
              <div className={styles.headingContainer}>{t('createOrganization.pageTitle')}</div>
              <div className={styles.headingDescriptionContainer}>{t('createOrganization.pageDescription')}</div>
            </div>
            <div className={styles.mainPicContainer}>
              <div className={styles.secondaryPicContainer}>
                <div className={styles.deskPic}>
                  <img src={Desk} alt={t('createOrganization.altText.desk')} />
                </div>
                <div className={styles.linePic}>
                  <img src={Line} alt={t('createOrganization.altText.line')} />
                </div>
                <div className={styles.notepadPic}>
                  <img src={Pad} alt={t('createOrganization.altText.notepad')} />
                </div>
                <div className={styles.tickPic}>
                  <img src={Tick} alt={t('createOrganization.altText.tick')} />
                </div>
                <div className={styles.checkeredPic}>
                  <img src={Checkered} alt={t('createOrganization.altText.checkered')} />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formFlexItem}>
            <div className={styles.linkedinForm}>
              <div className={styles.linkedinFormColumn}>
                <p className={styles.linkedinFieldLable}>{t('createOrganization.linkedinImport.title')}</p>
                <Formik initialValues={initialValues} onSubmit={importData}>
                  <Form>
                    <div className={styles.linkedinRow}>
                      <div className={styles.linkedinFieldContainer}>
                        <p className={styles.inputStarter}>{LINKEDIN_COMPANY_URL}</p>
                        <Field
                          name="url"
                          as={CustomTextField}
                          type="text"
                          placeholder={t('createOrganization.linkedinImport.placeholder')}
                          value={url}
                          onChange={handleUrlChange}
                          error={!isURLValid}
                          className={styles.urlInput}
                        />
                      </div>

                      <div className={styles.buttonPadding}>
                        <ButtonWithIcon
                          text={linkedInLoading ? t('common:importing') : t('common:import')}
                          icon={linkedInLoading ? <TailSpin height={20} width={20} color="#fff" /> : null}
                          img={linkedInLoading ? null : importIcon}
                          imgClassName="btn-icon-36"
                          textClassName="btn-icon-linkedin"
                          variant="linkedin"
                          disabled={!isURLValid || !url || linkedInLoading}
                          onClick={importData}
                        />
                      </div>
                    </div>
                  </Form>
                </Formik>
                <div className={styles.linkedinBrandingRow}>
                  <span className={styles.powerdByText}>{t('createOrganization.linkedinImport.poweredBy')}</span>
                  <img
                    src={linkedinLogo}
                    alt={t('createOrganization.altText.linkedin')}
                    className={styles.linkedinLogo}
                  />
                </div>
              </div>
              <div className={styles.flatLineContainer}>
                <div className={styles.flatLine} />
              </div>
            </div>

            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, setFieldValue }) => (
                <Form className={styles.formParentContainer}>
                  <div className={styles.formContainer}>
                    <div className={styles.companyLogoText}>{t('createOrganization.form.uploadLogo')}</div>
                    <div
                      className={styles.companyUploadPic}
                      role="button"
                      tabIndex={0}
                      onClick={triggerFileInput}
                      onKeyPress={triggerFileInput}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt={t('createOrganization.altText.logoPreview')}
                          className={styles.logoPreview}
                        />
                      ) : (
                        <>
                          <div className={styles.filePic}>
                            <img src={File} alt={t('createOrganization.altText.file')} />
                          </div>
                          <div className={styles.fileText}>{t('createOrganization.form.upload')}</div>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        id="fileInput"
                        ref={fileInputRef}
                        className={styles.fileInput}
                        onChange={(event) => handleLogoChange(event, setFieldValue)}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <UpdateFormikValues />
                    <div>
                      <Field
                        name="name"
                        id="name"
                        type="text"
                        label={t('createOrganization.form.companyName')}
                        as={CustomTextField}
                      />
                    </div>
                    <div>
                      <Field
                        name="overview"
                        id="overview"
                        as="textarea"
                        placeholder={t('createOrganization.form.description')}
                        className={styles.descriptionField}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Field
                        data={industryImport && industryImport[0]?.value}
                        name="industry_id"
                        id="industry_id"
                        as={CustomSelect}
                        className={styles.customSelectTop}
                        placeholder={t('createOrganization.form.industry')}
                        options={industryOptions}
                      />
                    </div>
                    <div>
                      <Field
                        value={industryLocation}
                        name="locations"
                        id="locations"
                        as={LocationSelect}
                        required="yes"
                        type="text"
                        linkedInImport="true"
                        label={t('createOrganization.form.location')}
                        className="form-control"
                        onChange={(locations) => handleLocationChange(locations, setFieldValue)}
                      />
                    </div>
                    <div>
                      <Field
                        data={organizationSize && organizationSize[0]?.value}
                        name="organization_size_id"
                        id="organization_size_id"
                        as={CustomSelect}
                        className={styles.customSelectTop}
                        placeholder={t('createOrganization.form.companySize')}
                        options={industrySizes}
                      />
                    </div>
                  </div>
                  <div className={styles.submitButton}>
                    <Button
                      variant="submit"
                      type="submit"
                      disabled={checkFormValues(values)}
                      className={!checkFormValues(values) ? styles.activeBg : ''}
                    >
                      {t('createOrganization.form.submit')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
